/** Format KB value as a human-readable size string. */
export function formatSize(kb) {
  if (kb === null || kb === undefined) return '—';
  if (kb < 1024) return `${Number(kb).toFixed(2)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

/** Format raw bytes as a human-readable size string. */
export function formatBytesAsSize(bytes) {
  return formatSize(bytes / 1024);
}

/**
 * Format seconds as a human-readable countdown.
 * > 1 day  → "6d 23h"
 * > 1 hour → "2h 15m"
 * otherwise → "M:SS"
 */
export function formatCountdown(seconds) {
  if (seconds === null || seconds === undefined) return '';
  if (seconds >= 86400) {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    return `${d}d ${h}h`;
  }
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Format a UTC date string as a relative label ("3m ago", "2h ago", etc.) */
export function formatRelativeDate(dateStr) {
  if (!dateStr) return '';
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  1) return 'Just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

/** Format an absolute date as a compact readable string — e.g. "20 Apr, 19:00". */
export function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day   = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const time  = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${month}, ${time}`;
}

/**
 * Trigger a browser file download.
 * For cross-origin URLs (like Shopify CDN), we fetch the file as a blob
 * to ensure the 'download' attribute is respected.
 */
export async function triggerDownload(url, filename) {
  try {
    // If it's a relative URL or same origin, try direct download first
    const isSameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);
    
    if (isSameOrigin) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // Cross-origin: fetch as blob to force download
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download failed:', err);
    // Fallback to direct navigation if fetch fails (e.g. CORS issues)
    window.open(url, '_blank');
  }
}

/** Badge tone for a manual-upload file status. */
export function getFileBadgeTone(status) {
  switch (status) {
    case 'done':
    case 'optimized':  return 'success';
    case 'failed':     return 'critical';
    case 'processing': return 'warning';
    case 'cancelled':  return 'attention';
    default:           return undefined;
  }
}

/** Display label for a manual-upload file status. */
export function getFileStatusLabel(status) {
  switch (status) {
    case 'done':
    case 'optimized':  return 'Optimized';
    case 'failed':     return 'Failed';
    case 'processing': return 'Processing';
    case 'cancelled':  return 'Aborted';
    default:           return 'Queued';
  }
}
